import { AbstractControl } from '@angular/forms';
import moment from 'moment';
import { Separators } from '../directives/mask-directive/mask-options';
import { DateFormatEnum } from '../models/date-format.enum';
import { DateInputEnum } from '../models/date-input.enum';

export function customMaskedDateValidator(inputType: DateInputEnum) {
    let letterGroup = '';
    switch (inputType) {
        case DateInputEnum.TIME:
            letterGroup = '[hms]';
            break;
        case DateInputEnum.DATE:
            letterGroup = '[dMy]';
            break;
        case DateInputEnum.DATE_TIME:
        default:
            letterGroup = '[hmsdMy]';
            break;
    }
    return (formControl: AbstractControl): { [message: string]: boolean } | null => {
        let output = null;
        const regExp = new RegExp(letterGroup);
        const splittedValue = formControl.value.split('');
        if (splittedValue.length < 1) {
            return { placeholder_error: true };
        }
        for (const i of splittedValue) {
            if (regExp.test(i) === true) {
                output = { placeholder_error: true };
                break;
            }
        }
        return output;
    };
}

export const valueToMoment = (value: any): moment.Moment | null => {
    if (typeof value !== 'string') return null;
    const parsed = moment(value);
    if (!parsed.isValid()) return null;
    return parsed;
};

export const getViewFormat = (
    inputType: DateInputEnum,
    inputFormat: DateFormatEnum,
    dateSeparator: string,
    timeSeparator: string,
): string => {
    const formats: Record<DateInputEnum, Record<DateFormatEnum, string>> = {
        [DateInputEnum.DATE_TIME]: {
            [DateFormatEnum.SHORT]: `dd${dateSeparator}MM${dateSeparator}yyyy, HH${timeSeparator}mm`,
            [DateFormatEnum.LONG]: 'dd MMMM, y HH:mm:ss',
        },
        [DateInputEnum.DATE]: {
            [DateFormatEnum.SHORT]: `dd${dateSeparator}MM${dateSeparator}yyyy`,
            [DateFormatEnum.LONG]: 'dd MMMM, y',
        },
        [DateInputEnum.TIME]: {
            [DateFormatEnum.SHORT]: `HH${timeSeparator}mm`,
            [DateFormatEnum.LONG]: 'HH:mm:ss',
        },
        [DateInputEnum.DATE_TIME_OLD]: {
            [DateFormatEnum.SHORT]: '',
            [DateFormatEnum.LONG]: '',
        },
    } satisfies Record<DateInputEnum, Record<DateFormatEnum, string>>;

    return formats[inputType]?.[inputFormat] ?? '';
};

export const dateFormatToConfiguration = (dateInputFormat: string): string => {
    let output: string;
    output = dateInputFormat ? dateInputFormat.replace(/YYYY/i, 'yy').replace(/[^dmy]/gi, '') : 'ddMMyy';
    return output;
};

export const timeFormatToConfiguration = (timeInputFormat: string): string => {
    let output: string;
    output = timeInputFormat ? timeInputFormat.toLowerCase().replace(':', '') : 'hhmm';
    return output;
};

export const dateTimeFormatToConfiguration = (dateTimeInputFormat: string): string => {
    let output: string;
    output = dateTimeInputFormat;
    // TODO: capire se ci serve
    return output;
};

export const getSeparator = (dateSeparator: string, defaultSeparator: Separators): Separators => {
    // || Separators.space
    let output: Separators;

    switch (dateSeparator) {
        case ':':
            output = Separators.colon;
            break;
        case '/':
            output = Separators.slash;
            break;
        case ' ':
            output = Separators.space;
            break;
        case '-':
            output = Separators.dash;
            break;
        case '.':
            output = Separators.dot;
            break;
        default:
            output = defaultSeparator;
            break;
    }
    return output;
};

/**
 * ISO (UTC instant) -> stringa "civile" che l'utente vede/modifica in locale
 * - DATE_TIME: YYYY-MM-DDTHH:mm:ss
 * - DATE:      YYYY-MM-DD
 * - TIME:      HH:mm:ss
 */
export function isoToMaskModel(iso: string | null, type: DateInputEnum): string | null {
    if (!iso) return null;

    const m = moment(iso);
    if (!m.isValid()) return null;

    const l = m.clone().local(); // ✅ UI locale

    const yyyy = String(l.year()).padStart(4, '0');
    const MM = String(l.month() + 1).padStart(2, '0');
    const dd = String(l.date()).padStart(2, '0');
    const HH = String(l.hour()).padStart(2, '0');
    const mm = String(l.minute()).padStart(2, '0');
    const ss = String(l.second()).padStart(2, '0');

    if (type === DateInputEnum.DATE_TIME) return `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}`;
    if (type === DateInputEnum.DATE) return `${yyyy}-${MM}-${dd}`;
    return `${HH}:${mm}:${ss}`;
}

/**
 * stringa "civile" locale -> ISO (UTC instant)
 * preserva le parti NON editate usando originalIso (sempre in locale):
 * - DATE: preserva time se c'era, altrimenti azzera 00:00
 * - TIME: preserva date se c'era, altrimenti 1970-01-01
 */
export function maskModelToIso(model: string, type: DateInputEnum, originalIso: string | null): string | null {
    const normalized = model.replace(/\s+/, 'T');

    const baseLocal =
        originalIso && moment(originalIso).isValid()
            ? moment(originalIso).local()
            : type === DateInputEnum.TIME
              ? moment('1970-01-01T00:00:00.000Z').local()
              : moment().local();

    if (type === DateInputEnum.DATE_TIME) {
        const mLocal = moment(normalized, moment.ISO_8601, true); // ✅ interpreta come locale
        if (!mLocal.isValid()) return originalIso;
        return mLocal.toISOString(); // ✅ salva UTC instant
    }

    if (type === DateInputEnum.DATE) {
        const dLocal = moment(normalized, 'YYYY-MM-DD', true);
        if (!dLocal.isValid()) return originalIso;

        baseLocal.set({
            year: dLocal.year(),
            month: dLocal.month(),
            date: dLocal.date(),
        });

        // se non c'era un precedente, per DATE di solito metti mezzanotte locale
        if (!originalIso) {
            baseLocal.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        }

        return baseLocal.toISOString();
    }

    // TIME: accetta HH:mm o HH:mm:ss
    const tLocal = moment(normalized, ['HH:mm:ss', 'HH:mm'], true);
    if (!tLocal.isValid()) return originalIso;

    baseLocal.set({
        hour: tLocal.hour(),
        minute: tLocal.minute(),
        second: normalized.length >= 8 ? tLocal.second() : 0,
        millisecond: 0,
    });

    return baseLocal.toISOString();
}
